import installTcfStub from '@iabtechlabtcf/stub'
import type { TcData } from './tcfConsent'

/**
 * InMobi Choice — the Google-certified IAB TCF consent platform behind the
 * cookie window (owner's decision 2026-11-03, TASK-1162), shown worldwide.
 * Free plan; its look comes from `inMobiChoiceTheme.css` (the platform's own
 * branding controls are paid from 2027-01-31).
 *
 * The account id is public by design: it is in every page's script URL.
 * The portal shows the account as `p-SweUN-pkSpA_W`, but the script path
 * takes the id WITHOUT the `p-` prefix (the official "Universal Tag",
 * verified 2026-11-03: `/choice/p-…/` answers 204, `/choice/SweUN-…/` the
 * script). The script is fetched per domain (`/choice/<id>/<domain>/…`), and
 * the platform answers 204 for a domain that has no property in the
 * account — the window then never shows and `__tcfapi` stays the stub, so
 * the site keeps counting the visitor as undecided.
 */
export const INMOBI_ACCOUNT_ID = 'SweUN-pkSpA_W'

export function inMobiChoiceScriptUrl(domain: string): string {
  return `https://cmp.inmobi.com/choice/${INMOBI_ACCOUNT_ID}/${domain}/choice.js?tag_version=V3`
}

/** The `__tcfapi` CMP API (TCF v2), as the stub and the platform expose it. */
export type TcfApi = (
  command: string,
  version: number,
  callback: (result: TcData, success: boolean) => void,
  parameter?: unknown,
) => void

declare global {
  interface Window {
    __tcfapi?: TcfApi
  }
}

/**
 * Install the IAB stub (so AdSense and our listener can queue calls at
 * once) and append the platform script for `domain`. Idempotent. Never
 * throws into the page: a failure leaves the visitor undecided.
 */
let loaded = false

export function loadInMobiChoice(domain: string, onError: () => void): void {
  if (loaded) return
  loaded = true
  try {
    if (!window.__tcfapi) installTcfStub()
    const script = document.createElement('script')
    script.async = true
    script.src = inMobiChoiceScriptUrl(domain)
    script.onerror = onError
    document.head.appendChild(script)
  } catch {
    onError()
  }
}

/** Subscribe to TCF events (`tcloaded`, `cmpuishown`, `useractioncomplete`). */
export function onTcfEvent(listener: (tcData: TcData) => void): void {
  try {
    window.__tcfapi?.('addEventListener', 2, (tcData, success) => {
      if (success) listener(tcData)
    })
  } catch {
    // The platform threw — consent stays undecided (safe-client #2).
  }
}

/**
 * The city's app icon and wordmark for the window's header (Figma "Dialog
 * header"), read by `inMobiChoiceTheme.css` as `--cmp-logo` /
 * `--cmp-logo-dark` / `--cmp-wordmark`: the platform's own logo field is a
 * paid feature, and CSS cannot know the city.
 */
export function brandConsentWindow(
  logo: { readonly light: string; readonly dark: string },
  wordmark: string,
): void {
  const style = document.documentElement.style
  style.setProperty('--cmp-logo', `url(${JSON.stringify(logo.light)})`)
  style.setProperty('--cmp-logo-dark', `url(${JSON.stringify(logo.dark)})`)
  style.setProperty('--cmp-wordmark', JSON.stringify(wordmark))
}

/** Re-open the window — the footer's "Cookie settings" (PECR: a choice can be changed). */
export function showConsentWindow(): void {
  try {
    window.__tcfapi?.('displayConsentUi', 2, () => {})
  } catch {
    // Nothing to show; the link is inert rather than broken.
  }
}

/** Whether this page runs the platform (the footer link shows only then). */
export function hasConsentWindow(): boolean {
  return typeof window !== 'undefined' && typeof window.__tcfapi === 'function'
}

/* @internal — test-only reset. */
export function __resetInMobiChoiceForTests(): void {
  loaded = false
}
